import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import {
  ArrowPathIcon,
  CheckIcon,
  ClipboardIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { blobSinkActionService } from '@/services/api/blobSinkActionService';
import { BlobSinkAction } from '@/services/types/blob-types';
import { getExternalSystemDisplayName } from '@/utils/externalSystemUtils';

import { formatDateString } from '../utils/fileUtils';
import { getSinkMetadataDisplayRows } from '../utils/sinkMetadataDisplay';

const getTargetSystemName = getExternalSystemDisplayName;

interface SinkActionStatusProps {
  sinkAction: BlobSinkAction;
  matterId: string;
  enableContinuousPolling?: boolean;
}

const POLLING_STATUSES = [
  'PENDING',
  'PROCESSING',
  'WAITING_FOR_PUSH_WINDOW',
  'READY_TO_PUSH',
  'PUSHING',
  'AWAITING_APPROVAL',
];

export const SinkActionStatus: React.FC<SinkActionStatusProps> = ({
  sinkAction: initialSinkAction,
  matterId,
  enableContinuousPolling = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isReasoningExpanded, setIsReasoningExpanded] = useState(false);
  const [shouldPoll, setShouldPoll] = useState(
    enableContinuousPolling ||
      POLLING_STATUSES.includes(initialSinkAction.status),
  );

  const [approveSinkAction, { isLoading: isApproving }] =
    blobSinkActionService.useBlobSinkActionsApproveMutation();
  const [rejectSinkAction, { isLoading: isRejecting }] =
    blobSinkActionService.useBlobSinkActionsRejectMutation();
  const [removeSinkAction, { isLoading: isRemoving }] =
    blobSinkActionService.useBlobSinkActionsRemoveMutation();
  const [repushSinkAction, { isLoading: isRepushing }] =
    blobSinkActionService.useBlobSinkActionsRepushMutation();

  // Poll for updates when status is in progress or awaiting approval
  const { data: polledSinkAction } =
    blobSinkActionService.useBlobSinkActionsRetrieveQuery(
      {
        matterId,
        blobId: initialSinkAction.blob_id,
        actionId: initialSinkAction.id,
      },
      {
        pollingInterval: shouldPoll
          ? enableContinuousPolling
            ? 10000
            : 5000
          : 0,
        skip: !shouldPoll,
        skipPollingIfUnfocused: true,
      },
    );

  // Stop polling when status changes to a non-polling status (unless continuous polling is enabled)
  useEffect(() => {
    if (
      !enableContinuousPolling &&
      polledSinkAction &&
      !POLLING_STATUSES.includes(polledSinkAction.status)
    ) {
      setShouldPoll(false);
    }
  }, [polledSinkAction, enableContinuousPolling]);

  // Use polled data merged with initial (to preserve fields like modified_at)
  const sinkAction: BlobSinkAction = polledSinkAction
    ? { ...initialSinkAction, ...polledSinkAction }
    : initialSinkAction;

  const isLoading = isApproving || isRejecting || isRemoving || isRepushing;

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    middleware: [offset(8), flip(), shift({ padding: 10 })],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const handleApprove = async () => {
    try {
      await approveSinkAction({
        matterId,
        blobId: sinkAction.blob_id,
        actionId: sinkAction.id,
      }).unwrap();
      toast.success('Sink action approved');
      setIsOpen(false);
    } catch (error: any) {
      const errorMessage =
        error?.data?.detail ||
        error?.data?.message ||
        error?.message ||
        'Failed to approve sink action';
      toast.error(errorMessage);
    }
  };

  const handleReject = async () => {
    try {
      await rejectSinkAction({
        matterId,
        blobId: sinkAction.blob_id,
        actionId: sinkAction.id,
        rejectActionRequest: {},
      }).unwrap();
      toast.success('Sink action rejected');
      setIsOpen(false);
    } catch (error: any) {
      const errorMessage =
        error?.data?.detail ||
        error?.data?.message ||
        error?.message ||
        'Failed to reject sink action';
      toast.error(errorMessage);
    }
  };

  const handleRemove = async () => {
    const targetName = getTargetSystemName(sinkAction.target_system);
    try {
      await removeSinkAction({
        matterId,
        blobId: sinkAction.blob_id,
        actionId: sinkAction.id,
      }).unwrap();
      toast.success(`Document removed from ${targetName}`);
      setIsOpen(false);
    } catch (error: any) {
      const errorMessage =
        error?.data?.detail ||
        error?.data?.message ||
        error?.message ||
        `Failed to remove document from ${targetName}`;
      toast.error(errorMessage);
    }
  };

  const handleRetry = async () => {
    const targetName = getTargetSystemName(sinkAction.target_system);
    try {
      await repushSinkAction({
        matterId,
        blobId: sinkAction.blob_id,
        actionId: sinkAction.id,
      }).unwrap();
      toast.success(`Retrying push to ${targetName}`);
      setIsOpen(false);
    } catch (error: any) {
      const errorMessage =
        error?.data?.detail ||
        error?.data?.message ||
        error?.message ||
        'Failed to retry push';
      toast.error(errorMessage);
    }
  };

  const getStatusDisplay = () => {
    const targetName = getTargetSystemName(sinkAction.target_system);
    switch (sinkAction.status) {
      case 'PUSHED':
        return {
          text: `Pushed to ${targetName} ${formatDateString(sinkAction.modified_at, 'relative')}`,
          color: 'text-green-700',
        };
      case 'ALREADY_EXISTS':
        return {
          text: `Already in ${targetName}`,
          color: 'text-green-700',
        };
      case 'REMOVED':
        return {
          text: `Removed from ${targetName} ${formatDateString(sinkAction.modified_at, 'relative')}`,
          color: 'text-slate-600',
        };
      case 'FAILED':
        return {
          text: `Failed to push ${formatDateString(sinkAction.modified_at, 'relative')}`,
          color: 'text-red-600',
        };
      case 'AWAITING_APPROVAL':
        return {
          text: 'Requires human approval',
          color: 'text-amber-600',
        };
      case 'REJECTED':
        return {
          text: 'Push rejected',
          color: 'text-slate-500',
        };
      case 'PENDING':
      case 'PROCESSING':
      case 'WAITING_FOR_PUSH_WINDOW':
      case 'READY_TO_PUSH':
      case 'PUSHING':
        return {
          text:
            sinkAction.status === 'WAITING_FOR_PUSH_WINDOW'
              ? `Waiting for scheduled push window (${targetName})...`
              : `Pushing to ${targetName}...`,
          color: 'text-blue-600',
        };
      default:
        return null;
    }
  };

  const statusDisplay = getStatusDisplay();

  if (!statusDisplay) {
    return null;
  }

  const isAwaitingApproval = sinkAction.status === 'AWAITING_APPROVAL';
  const isFailed = sinkAction.status === 'FAILED';
  const isRejected = sinkAction.status === 'REJECTED';
  const isPushed = sinkAction.status === 'PUSHED';
  const isRemoved = sinkAction.status === 'REMOVED';
  const canApprove = isAwaitingApproval || isRejected;
  const canRetry = isFailed || isRemoved;
  const metadataRows = getSinkMetadataDisplayRows(sinkAction);

  return (
    <>
      <button
        ref={refs.setReference}
        type="button"
        className={`text-[10px] leading-none ${statusDisplay.color} hover:underline cursor-pointer`}
        {...getReferenceProps()}
      >
        {statusDisplay.text}
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            role="dialog"
            className="z-[9999] w-72 bg-white border border-slate-200 rounded-lg shadow-lg p-3"
          >
            <div className="space-y-2 group/panel">
              {/* Status header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span
                    className={`font-medium text-sm ${statusDisplay.color}`}
                  >
                    {sinkAction.status === 'PUSHED'
                      ? 'Pushed'
                      : sinkAction.status === 'ALREADY_EXISTS'
                        ? 'Already exists'
                        : sinkAction.status === 'REMOVED'
                          ? 'Removed'
                          : sinkAction.status === 'FAILED'
                            ? 'Failed'
                            : sinkAction.status === 'AWAITING_APPROVAL'
                              ? 'Awaiting approval'
                              : sinkAction.status === 'REJECTED'
                                ? 'Rejected'
                                : sinkAction.status === 'WAITING_FOR_PUSH_WINDOW'
                                  ? 'Waiting for push window'
                                : 'In progress'}
                  </span>
                </div>
                {sinkAction.last_workflow_run_id && (
                  <button
                    type="button"
                    className="opacity-0 group-hover/panel:opacity-100 p-1 text-slate-300 hover:text-slate-500 transition-opacity"
                    onClick={() => {
                      if (sinkAction.last_workflow_run_id) {
                        navigator.clipboard.writeText(
                          sinkAction.last_workflow_run_id,
                        );
                      }
                    }}
                    title="Copy workflow ID"
                  >
                    <ClipboardIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Timestamp */}
              <div className="text-xs">
                <span className="text-muted-foreground">
                  {sinkAction.status === 'PUSHED'
                    ? 'Pushed at: '
                    : sinkAction.status === 'ALREADY_EXISTS'
                      ? 'Checked at: '
                      : sinkAction.status === 'REMOVED'
                        ? 'Removed at: '
                        : 'Updated at: '}
                </span>
                <span className="font-medium">
                  {formatDateString(sinkAction.modified_at, 'short')}
                </span>
              </div>

              {/* File name */}
              {sinkAction.target_name && (
                <div className="text-xs">
                  <span className="text-muted-foreground">File: </span>
                  <span className="font-medium">{sinkAction.target_name}</span>
                </div>
              )}

              {/* Destination path */}
              {sinkAction.target_path && (
                <div className="text-xs">
                  <span className="text-muted-foreground">Destination: </span>
                  <span className="font-medium">{sinkAction.target_path}</span>
                </div>
              )}

              {metadataRows.map((row) => (
                <div key={row.label} className="text-xs">
                  <span className="text-muted-foreground">{row.label}: </span>
                  <span className="font-medium">{row.value}</span>
                </div>
              ))}

              {/* Confidence score */}
              {sinkAction.target_confidence != null && (
                <div className="text-xs">
                  <span className="text-muted-foreground">Confidence: </span>
                  <span className="font-medium">
                    {Math.round(sinkAction.target_confidence * 100)}%
                  </span>
                </div>
              )}

              {/* Reasoning */}
              {sinkAction.target_reasoning && (
                <div className="text-xs">
                  <span className="text-muted-foreground">Reasoning: </span>
                  {sinkAction.target_reasoning.length > 100 ? (
                    <>
                      <span>
                        {isReasoningExpanded
                          ? sinkAction.target_reasoning
                          : `${sinkAction.target_reasoning.slice(0, 100)}...`}
                      </span>
                      <button
                        type="button"
                        className="ml-1 text-blue-600 hover:text-blue-700 hover:underline"
                        onClick={() =>
                          setIsReasoningExpanded(!isReasoningExpanded)
                        }
                      >
                        {isReasoningExpanded ? 'show less' : 'show more'}
                      </button>
                    </>
                  ) : (
                    <span>{sinkAction.target_reasoning}</span>
                  )}
                </div>
              )}

              {/* Error message (only for failed) */}
              {isFailed &&
                (!!sinkAction.metadata?.error_message ||
                  !!sinkAction.metadata?.error) && (
                  <div className="text-xs">
                    <span className="text-muted-foreground">Error: </span>
                    <span className="font-medium text-red-600">
                      {String(
                        (sinkAction.metadata?.error_message ||
                          sinkAction.metadata?.error) ??
                          '',
                      )}
                    </span>
                  </div>
                )}

              {/* Approve/Reject actions */}
              {canApprove && (
                <div className="flex items-center gap-2 pt-2 mt-1 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    className={`${isRejected ? 'w-full' : 'flex-1'} h-8 border-slate-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-200`}
                    onClick={handleApprove}
                    disabled={isLoading}
                  >
                    <CheckIcon className="w-3.5 h-3.5 mr-1.5" />
                    {isRejected ? 'Approve & Push' : 'Approve'}
                  </Button>
                  {isAwaitingApproval && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 h-8 border-slate-200 text-rose-700 hover:bg-rose-50 hover:border-rose-200"
                      onClick={handleReject}
                      disabled={isLoading}
                    >
                      <XMarkIcon className="w-3.5 h-3.5 mr-1.5" />
                      Reject
                    </Button>
                  )}
                </div>
              )}

              {/* Remove action (only for pushed) */}
              {isPushed && (
                <div className="flex items-center gap-2 pt-2 mt-1 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full h-8 border-slate-200 text-red-700 hover:bg-red-50 hover:border-red-200"
                    onClick={handleRemove}
                    disabled={isLoading}
                  >
                    <TrashIcon className="w-3.5 h-3.5 mr-1.5" />
                    Remove from {getTargetSystemName(sinkAction.target_system)}
                  </Button>
                </div>
              )}

              {/* Retry action (only for failed or removed) */}
              {canRetry && (
                <div className="flex items-center gap-2 pt-2 mt-1 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full h-8 border-slate-200 text-blue-700 hover:bg-blue-50 hover:border-blue-200"
                    onClick={handleRetry}
                    disabled={isLoading}
                  >
                    <ArrowPathIcon className="w-3.5 h-3.5 mr-1.5" />
                    Retry Push
                  </Button>
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};
